import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';

import {useSearchParams} from 'react-router-dom';
import {createSearchParams, useNavigate} from 'react-router-dom';
import {
	funcDecodeHex24ToDocId,
	funcDecodeHex24ToEntity,
	InfinityListColumn,
	InfinityListColumnMetaData,
	InfinityListFilter,
	InfinityListURLParams,
	InfinityListView,
} from 'components/InfinityList/InfinityList';
import {DEFAULT_DATE} from 'components/EntityList/EntityUtils';

import {v4 as uuidv4} from 'uuid';
import {AdvancedFilterTreeNode} from './InfinityListAdvancedFilter';
import {FilterOption} from 'types';
import {alphabeticalSort, numericalSort} from 'helpers/sortHelpers';
import {TFunction} from 'i18next';

interface Identifiable {
	id: string;
}
interface DataStoreDocument<T extends Identifiable> {
	[key: string]: T;
}
interface DataStoreSortOrders {
	[key: string]: string[];
}

interface IHookInput<T extends Identifiable> {
	views: InfinityListView[];
	intBatchSize: number | undefined;
	initialSortField: string;
	initialSortOrder: string;
	initialView: string;
	generateLink?: boolean;
	arrIds: string[];
	columns: InfinityListColumn[];
	columnMetaData: InfinityListColumnMetaData[];
	filters2MongoQuery?: (allFilters: InfinityListFilter[]) => any;
	filters2Link?: (allFilters: InfinityListFilter[]) => InfinityListURLParams;
	link2Filter?: (searchParams: URLSearchParams) => InfinityListFilter[];
	combineViewAndUserFilterQuery?: (
		query: any,
		views: InfinityListView[],
		viewKey?: string,
	) => any;
	queryDocumentsDB: (
		query: any,
		sortField: string,
		sortOrder: string,
		preFilteredIds?: string[],
	) => Promise<{arrDocumentNext: T[]; arrIDsNew: string[]}>;
	getDocumentsNextDB: (preFilteredIds: string[]) => Promise<T[]>;
	getSortOrderDB: (
		preFilteredIds: string[],
		sortField: string,
	) => Promise<string[]>;
	handleExcelExport?: (arrDocuments: T[]) => Promise<void>;
	applyFaqQuestionCounter?: (items: T[]) => Promise<T[]>;
}

interface IHookOutput<T extends Identifiable> {
	arrDocument: T[];
	keyPrefix: string;
	linksFilters: InfinityListFilter[];
	setArrDocument: (arrDocument: T[]) => void;
	setlastFetchedId: (lastFetchedId: string) => void;
	setCursor: (cursor: number) => void;
	firstRenderSTD: () => Promise<void>;
	applyFilterSTD: (
		allFilters: InfinityListFilter[],
		sortField: string,
		sortOrder: string,
		currentView?: string,
		preFilteredIds?: string[],
		advancedFilterOn?: boolean,
		advancedFilterNodeList?: AdvancedFilterTreeNode[],
	) => Promise<void>;
	applySortSTD: (
		sortField: string,
		sortOrder: string | undefined,
		setSortOrder: (sortOrder: string) => void,
	) => Promise<void>;
	loadItemsNextSTD: (
		currentId: string,
		sortField: string,
		sortOrder: string,
	) => Promise<void>;
	excelExportSTD: (sortField: string, sortOrder: string) => Promise<void>;
}

const intBatchSizeSTD = 40;

export function useInfinityListStandardHooks<T extends Identifiable>({
	views,
	intBatchSize,
	initialSortField,
	initialSortOrder,
	initialView,
	generateLink,
	columns,
	columnMetaData,
	arrIds,
	filters2MongoQuery,
	filters2Link,
	link2Filter,
	combineViewAndUserFilterQuery,
	queryDocumentsDB,
	getDocumentsNextDB,
	getSortOrderDB,
	handleExcelExport,
	applyFaqQuestionCounter,
}: IHookInput<T>): IHookOutput<T> {
	const [lastFetchedId, setlastFetchedId] = useState('');

	const [cursor, setCursor] = useState(0);

	const [keyPrefix, setKeyPrefix] = useState(uuidv4());

	const [dsDocument, setDSDocument] = useState<DataStoreDocument<T>>({});

	const [dsSortOrders, setDSSortOrders] = useState<DataStoreSortOrders>({});

	const [arrDocument, setArrDocument] = useState<T[]>([]);

	const [searchParams] = useSearchParams();

	const navigate = useNavigate();

	const filters2MongoQueryFinal =
		filters2MongoQuery ?? filters2MongoQuerySTD(columns, columnMetaData);

	const filters2LinkFinal =
		filters2Link ?? filters2LinkSTD(columns, columnMetaData);
	const link2FilterFinal =
		link2Filter ?? link2FilterSTD(columns, columnMetaData);

	const filterTree2MongoQuery = filterTree2MongoQuerySTD(
		columns,
		columnMetaData,
	);

	// *metadata--------------------------------------------------------------------------------------------------------------------
	const fieldsSort = columns.filter(x => x.sortable === true).map(x => x.key);
	const fieldsSortInverted = columnMetaData
		.filter(x => x.sortInverted === true)
		.map(x => x.key);
	const linksFilters = useMemo(() => {
		return link2FilterFinal(searchParams);
	}, [searchParams]);

	const queryLink = useMemo(
		() => filters2MongoQueryFinal(linksFilters),
		[linksFilters],
	);

	// *functions for summarizing prop functions --------------------------------------------------------------------------------------
	const getSortIds = async (lstFilteredIds: string[], sortField: string) => {
		const boolSortisCalculated =
			dsSortOrders[sortField] !== undefined &&
			dsSortOrders[sortField].length > 0;

		if (boolSortisCalculated) {
			return dsSortOrders[sortField];
		}
		const sortOrder = await getSortOrderDB(lstFilteredIds, sortField);

		if (sortOrder) {
			dsSortOrders[sortField] = sortOrder;
			setDSSortOrders({...dsSortOrders});
		}

		return sortOrder ?? lstFilteredIds;
	};

	const getSortOrderDefault = (sortField: string) => {
		return fieldsSortInverted.includes(sortField) ? 'DESC' : 'ASC';
	};

	const getSortOrderServer = (
		sortField: string,
		sortOrder: string | undefined,
	) => {
		return (fieldsSortInverted.includes(sortField) && sortOrder === 'ASC') ||
			(!fieldsSortInverted.includes(sortField) && sortOrder === 'DESC')
			? 'DESC'
			: 'ASC';
	};

	const getSortInverted = (
		sortField: string,
		sortOrder: string | undefined,
	) => {
		return (
			(fieldsSortInverted.includes(sortField) && sortOrder === 'ASC') ||
			(!fieldsSortInverted.includes(sortField) && sortOrder === 'DESC')
		);
	};

	const getIdsNext = async (
		arrIds: string[],
		sortField: string,
		sortOrderEff: string,
		start: number,
		end: number,
	) => {
		const arrSortIdsDefault = await getSortIds(arrIds, sortField);
		if (getSortInverted(sortField, sortOrderEff)) {
			return arrSortIdsDefault.slice().reverse().slice(start, end);
		}
		return arrSortIdsDefault.slice(start, end);
	};

	const fetchServerNextData = async (arrIdsNext: string[]) => {
		const arrIdsToFetch = arrIdsNext.filter(x => dsDocument[x] === undefined);

		if (arrIdsToFetch.length > 0) {
			const dsDocumentNext: DataStoreDocument<T> = {};

			let fetchedDocuments = await getDocumentsNextDB(arrIdsToFetch);

			if (applyFaqQuestionCounter) {
				fetchedDocuments = await applyFaqQuestionCounter(fetchedDocuments);
			}

			fetchedDocuments.forEach(x => {
				dsDocumentNext[funcDecodeHex24ToDocId(x.id)] = x;
			});

			const dsDocumentNew = {
				...dsDocument,
				...dsDocumentNext,
			};

			const arrDocumentNext = arrIdsNext
				.map(x => dsDocumentNew[x])
				.filter(x => x !== undefined);

			setDSDocument(dsDocumentNew);

			return {dsDocumentNew, arrDocumentNext};
		}

		const arrDocumentNext = arrIdsNext
			.map(x => dsDocument[x])
			.filter(x => x !== undefined);

		return {dsDocument, arrDocumentNext};
	};

	const combineViewAndUserFilterQueryFinal =
		combineViewAndUserFilterQuery ?? combineViewAndUserFilterQuerySTD;

	// *prop functions ----------------------------------------------------------------------------------------------------------------
	const firstRenderSTD = async () => {
		// *firstRender called only once at the beginning
		const dsSortOrdersNext: DataStoreSortOrders = {};
		const dsDocumentNext: DataStoreDocument<T> = {};

		const sortOrderServer = getSortOrderServer(
			initialSortField,
			initialSortOrder,
		);

		const queryFinal = combineViewAndUserFilterQueryFinal(
			queryLink,
			views,
			initialView,
		);

		let {arrDocumentNext, arrIDsNew} = await queryDocumentsDB(
			queryFinal,
			initialSortField,
			sortOrderServer,
		);

		if (applyFaqQuestionCounter) {
			arrDocumentNext = await applyFaqQuestionCounter(arrDocumentNext);
		}

		arrDocumentNext.forEach(x => {
			dsDocumentNext[funcDecodeHex24ToDocId(x.id)] = x;
		});

		const lastFetchdIdNext = funcDecodeHex24ToDocId(
			arrDocumentNext.at(-1)?.id ?? '',
		);

		fieldsSort.forEach(x => {
			dsSortOrdersNext[x] = [];
		});

		dsSortOrdersNext[initialSortField] = arrIDsNew;

		setDSDocument(dsDocumentNext);

		setDSSortOrders(dsSortOrdersNext);

		setArrDocument(arrDocumentNext);

		setlastFetchedId(lastFetchdIdNext);

		setCursor(arrDocumentNext.length);
	};

	const applyFilterSTD = async (
		allFilters: InfinityListFilter[],
		sortField: string,
		sortOrder: string,
		currentView?: string,
		preFilteredIds?: string[],
		advancedFilterOn?: boolean,
		advancedFilterNodeList?: AdvancedFilterTreeNode[],
	) => {
		const dsSortOrdersNext: DataStoreSortOrders = {};
		const dsDocumentNext: DataStoreDocument<T> = {};

		const isAdvancedFilterOn = advancedFilterOn === true;

		const queryFilter = isAdvancedFilterOn
			? filterTree2MongoQuery(advancedFilterNodeList ?? [])
			: filters2MongoQueryFinal(allFilters);
		const queryFinal = combineViewAndUserFilterQueryFinal(
			queryFilter,
			views,
			currentView,
		);
		const sortOrderServer = getSortOrderServer(sortField, sortOrder);
		let {arrDocumentNext, arrIDsNew} = await queryDocumentsDB(
			queryFinal,
			sortField,
			sortOrderServer,
			preFilteredIds,
		);

		if (applyFaqQuestionCounter) {
			arrDocumentNext = await applyFaqQuestionCounter(arrDocumentNext);
		}

		if (!isAdvancedFilterOn && generateLink !== false) {
			const dataURL = filters2LinkFinal(allFilters);
			dataURL.view = [currentView ?? 'STD'];
			navigate({
				pathname: '',
				search: createSearchParams(dataURL).toString(),
			});
		}

		arrDocumentNext.forEach(x => {
			dsDocumentNext[funcDecodeHex24ToDocId(x.id)] = x;
		});

		const lastFetchdIdNext = funcDecodeHex24ToDocId(
			arrDocumentNext.at(-1)?.id ?? '',
		);

		fieldsSort.forEach(x => {
			dsSortOrdersNext[x] = [];
		});

		dsSortOrdersNext[initialSortField] = arrIDsNew;

		setDSDocument({
			...dsDocument,
			...dsDocumentNext,
		});

		setDSSortOrders(dsSortOrdersNext);

		setArrDocument(arrDocumentNext);

		setlastFetchedId(lastFetchdIdNext);

		setCursor(arrDocumentNext.length);

		setKeyPrefix(uuidv4());
	};

	const applySortSTD = useCallback(
		async (
			sortField: string,
			sortOrder: string | undefined,
			setSortOrder: (sortOrder: string) => void,
		) => {
			const sortOrderEff = sortOrder
				? sortOrder
				: getSortOrderDefault(sortField);

			const arrIdsNext = await getIdsNext(
				arrIds,
				sortField,
				sortOrderEff,
				0,
				intBatchSize ?? intBatchSizeSTD,
			);

			setSortOrder(sortOrderEff);

			if (arrIdsNext.length > 0) {
				const {dsDocumentNew, arrDocumentNext} = await fetchServerNextData(
					arrIdsNext,
				);

				const lastIdNext = arrIdsNext.at(-1) ?? '';

				setArrDocument(arrDocumentNext);

				setlastFetchedId(lastIdNext);

				setCursor(arrIdsNext.length);
				setKeyPrefix(uuidv4());
			}
		},
		[arrIds, fetchServerNextData],
	);

	const loadItemsNextSTD = useCallback(
		async (currentId: string, sortField: string, sortOrder: string) => {
			if (funcDecodeHex24ToDocId(currentId) === lastFetchedId) {
				// *currentId is equal to known lastFetchedId (last row has been mounted)
				// 		*ids are known -> fetch next batch -> set lastFetchdId and cursor correctly
				const sortOrderEff = sortOrder
					? sortOrder
					: getSortOrderDefault(sortField);

				const intEnd = cursor + (intBatchSize ?? intBatchSizeSTD);

				const arrIdsNext = await getIdsNext(
					arrIds,
					sortField,
					sortOrderEff,
					cursor,
					intEnd,
				);

				if (arrIdsNext.length > 0) {
					const {dsDocumentNew, arrDocumentNext} = await fetchServerNextData(
						arrIdsNext,
					);

					const lastIdNext = arrIdsNext.at(-1) ?? '';

					setlastFetchedId(lastIdNext);

					setCursor(intEnd);

					setArrDocument([...arrDocument, ...arrDocumentNext]);
				}
			}
		},
		[arrIds, arrDocument, lastFetchedId, getIdsNext, fetchServerNextData],
	);

	const excelExportSTD = useCallback(
		async (sortField: string, sortOrder: string) => {
			if (handleExcelExport) {
				const sortOrderEff = sortOrder
					? sortOrder
					: getSortOrderDefault(sortField);

				const arrIdsNext = await getIdsNext(
					arrIds,
					sortField,
					sortOrderEff,
					0,
					arrIds.length,
				);
				const {arrDocumentNext} = await fetchServerNextData(arrIdsNext);

				handleExcelExport(arrDocumentNext);
			}
		},
		[
			arrIds,
			arrDocument,
			lastFetchedId,
			getIdsNext,
			fetchServerNextData,
			handleExcelExport,
		],
	);

	return {
		arrDocument,
		keyPrefix,
		linksFilters,
		setArrDocument,
		setlastFetchedId,
		setCursor,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
		excelExportSTD,
	};
}

export const queryStringMongoArraySTD = (
	nameMongoDBField?: string,
	isIDField?: boolean,
): ((filter: InfinityListFilter) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		const operation = filter.operation === 'eq' ? '$in' : '$nin';

		if (isIDField) {
			return `{ _id:{${operation}:[ObjectId('${filter.selection}')]}}`;
		}

		return `{'${nameMongoDBField ?? filter.columnKey}': {${operation}:['${
			filter.selection
		}']}}`;
	};

	return handleConversion;
};

export const queryStringMongoEnumSTD = (
	nameMongoDBField?: string,
): ((filter: InfinityListFilter) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		const operation = filter.operation === 'eq' ? '$in' : '$nin';
		return `{'${nameMongoDBField ?? filter.columnKey}': {${operation}:[${
			filter.selection
		}]}}`;
	};

	return handleConversion;
};

export const queryStringMongoDateSTD = (
	nameMongoDBField?: string,
): ((filter: InfinityListFilter) => string) => {
	const handleConversion = (filter: InfinityListFilter) => {
		return `{'${nameMongoDBField ?? filter.columnKey}': {$${
			filter.operation
		}:ISODate('${filter.selection.slice(0, 10)}')}}`;
	};

	return handleConversion;
};
export const filters2MongoQuerySTD =
	(
		columns: InfinityListColumn[],
		columnMetaData: InfinityListColumnMetaData[],
	) =>
	(allFilters: InfinityListFilter[]) => {
		if (allFilters.length === 0) {
			return '';
		}

		const arrQueryStrings: string[] = [];

		const arrayFields = columns
			.filter(x => x.filterType === 'Array')
			.map(x => x.key);
		const dateFilterFields = columns
			.filter(x => x.filterType === 'Date')
			.map(x => x.key);

		arrayFields
			.map((x1, index) => {
				const lstOptions = allFilters.filter(x2 => x2.columnKey === x1);
				const {mongoDBFn} = columnMetaData.find(x2 => x2.key === x1)!;

				if (lstOptions.length === 0) {
					return '';
				}
				return `{$or:[${lstOptions.map(x => mongoDBFn(x)).join(', ')}]}`;
			})
			.filter(x1 => x1 !== '')
			.forEach(x1 => arrQueryStrings.push(x1));

		dateFilterFields
			.map((x1, index) => {
				const {mongoDBFn} = columnMetaData.find(x2 => x2.key === x1)!;
				const dateFrom = allFilters.find(
					x2 => x2.columnKey === x1 && x2.operation === 'gte',
				);
				const dateTo = allFilters.find(
					x2 => x2.columnKey === x1 && x2.operation === 'lte',
				);

				if (dateFrom && dateTo) {
					return `{$and:[ ${mongoDBFn(dateFrom)}, ${mongoDBFn(dateTo)}]}`;
				}
				if (dateFrom) {
					return mongoDBFn(dateFrom);
				}
				if (dateTo) {
					return mongoDBFn(dateTo);
				}

				return '';
			})
			.filter(x1 => x1 !== '')
			.forEach(x1 => arrQueryStrings.push(x1));

		const queryString = arrQueryStrings.join(', ');

		if (arrQueryStrings.length === 0) {
			return '';
		}

		if (arrQueryStrings.length === 1) {
			return queryString;
		}

		return `{$and:[${queryString}]}`;
	};

const filterTree2MongoQuerySTD = (
	columns: InfinityListColumn[],
	columnMetaData: InfinityListColumnMetaData[],
): ((advancedFilterNodeList: AdvancedFilterTreeNode[]) => string) => {
	const filterTree2MongoQuery = (
		advancedFilterNodeList: AdvancedFilterTreeNode[],
	) => {
		if (advancedFilterNodeList.length > 0) {
			const rootNode = advancedFilterNodeList.at(0)!;
			return getMongoDBString(rootNode, advancedFilterNodeList, columnMetaData);
		}
		return '';
	};

	return filterTree2MongoQuery;
};
const getMongoDBString = (
	parentNode: AdvancedFilterTreeNode,
	advancedFilterNodeList: AdvancedFilterTreeNode[],
	columnMetaData: InfinityListColumnMetaData[],
): string => {
	const childFilterNodes = advancedFilterNodeList.filter(
		x => x.parentIndex === parentNode.currentIndex && x.isFilterNode === true,
	);
	const childLogicalNode = advancedFilterNodeList.filter(
		x => x.parentIndex === parentNode.currentIndex && x.isLogicalNode === true,
	);
	if (childFilterNodes.length > 0 || childLogicalNode.length > 0) {
		const filterNodeString = childFilterNodes
			.filter(x => x.filter)
			.map(x => {
				const {mongoDBFn} = columnMetaData.find(
					x2 => x2.key === x.filter?.columnKey,
				)!;
				return mongoDBFn(x.filter!);
			});
		const logicalNodeString = childLogicalNode.map(x =>
			getMongoDBString(x, advancedFilterNodeList, columnMetaData),
		);
		return `{$${parentNode.logicalOperator}: [${[
			...filterNodeString,
			...logicalNodeString,
		].join(', ')}] }`;
	}
	return '';
};

export const combineViewAndUserFilterQuerySTD = (
	queryUser: string,
	views: InfinityListView[],
	viewKey?: string,
) => {
	const viewKeyFinal = viewKey ?? 'STD';
	const view: InfinityListView | undefined = views.find(
		x => x.accessKey === viewKeyFinal,
	);
	const queryView = view?.query ?? '';

	if (queryUser === '') {
		return queryView;
	}

	if (queryView === '') {
		return queryUser;
	}

	return `{$and:[${queryView},${queryUser}]}`;
};

export const filters2LinkSTD = (
	columns: InfinityListColumn[],
	columnMetaData: InfinityListColumnMetaData[],
): ((allFilters: InfinityListFilter[]) => InfinityListURLParams) => {
	const filters2Link = (allFilters: InfinityListFilter[]) => {
		const dataURL: InfinityListURLParams = {};
		const dateFilterFields = columns
			.filter(x => x.filterType === 'Date')
			.map(x => x.key);
		allFilters.forEach(x => {
			const linkField =
				columnMetaData.find(y => y.key === x.columnKey)?.linkName ??
				x.columnKey;
			const isDateColumn = dateFilterFields.includes(x.columnKey);
			let value = '';
			if (isDateColumn) {
				value = `${x.operation}_${x.selection.slice(0, 10)}`;
			} else {
				value = x.selection;
			}

			if (dataURL[linkField] === undefined) {
				dataURL[linkField] = [value];
			} else {
				dataURL[linkField].push(value);
			}
		});

		return dataURL;
	};
	return filters2Link;
};

export const link2FilterSTD = (
	columns: InfinityListColumn[],
	columnMetaData: InfinityListColumnMetaData[],
): ((searchParams: URLSearchParams) => InfinityListFilter[]) => {
	const link2FilterSTD = (searchParams: URLSearchParams) => {
		const result: InfinityListFilter[] = [];

		const filterFields = columns.map(x => {
			const cMetaData = columnMetaData.find(y => y.key === x.key);
			if (cMetaData) {
				return {
					key: cMetaData.key,
					linkName: cMetaData.linkName ?? cMetaData.key,
					filterType: x.filterType,
				};
			}
			return {
				key: 'default',
				linkName: 'default',
				filterType: 'default',
			};
		});

		filterFields
			.filter(x => x.filterType === 'Array')
			.filter(x1 => searchParams.getAll(x1.linkName).length > 0)
			.forEach(x1 => {
				searchParams.getAll(x1.linkName).forEach(x2 =>
					result.push({
						columnKey: x1.key,
						selection: x2,
						display: 'UNKNOWN',
						operation: 'eq',
					}),
				);
			});

		filterFields
			.filter(x => x.filterType === 'Date')
			.filter(x1 => searchParams.getAll(x1.linkName).length > 0)
			.forEach(x1 => {
				searchParams.getAll(x1.linkName).forEach(x2 =>
					result.push({
						columnKey: x1.key,
						selection: x2.split('_').at(1) ?? DEFAULT_DATE,
						display:
							(x2.split('_').at(0) === 'gte' ? '>' : '') +
							(x2.split('_').at(0) === 'lte' ? '<' : '') +
							(x2.split('_').at(1) ?? DEFAULT_DATE),
						operation: x2.split('_').at(0) ?? 'gte',
					}),
				);
			});

		return result;
	};
	return link2FilterSTD;
};

export const getOptionsSorted = (
	optionsAll: any[],
	options: FilterOption[],
) => {
	const dictSortPrio: {[key: string]: number | null | undefined} = {};
	(optionsAll ?? []).forEach(x => {
		dictSortPrio[x.name] = x.sortPriority;
	});

	return optionsAll.length > 0
		? numericalSort(
				options.map(x => {
					return {...x, sortPriority: dictSortPrio[x.name]};
				}),
				'sortPriority',
		  ).map(x => {
				return {name: x.name, optionKey: x.optionKey};
		  })
		: alphabeticalSort(options, 'name');
};

export const addFavoriteView = (
	views: InfinityListView[],
	favoriteIds: {[key: string]: boolean} | undefined,
	entity: string,
	tFav: TFunction,
) => {
	if (favoriteIds) {
		const favoriteRegIds = Object.keys(favoriteIds).filter(
			x => funcDecodeHex24ToEntity(x) === entity,
		);
		if (favoriteRegIds.length > 0) {
			const queryIds = favoriteRegIds
				.map(x => `ObjectId('${funcDecodeHex24ToDocId(x)}')`)
				.join(',');
			views.push({
				headerText: tFav('Favorites'),
				accessKey: 'FAV',
				query: `{_id:{$in:[${queryIds}]}}`,
			});
		}
	}
};
